import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, Row, Col, Tooltip, OverlayTrigger, Spinner } from "react-bootstrap";
import Button from "src/components/Button/Button";
import styled from "styled-components";
import ResponsiveImage from "../ImageWrapper/ResponsiveImage";
import { formatDate, formatTime } from "src/utils/formatters";
import {
  convertTimeTo24HourFormat,
  formatTimeString
} from "src/utils/timeUtils";
import { determinePromoStatus, addFourHoursPatch } from "src/utils/promoUtils";
import { getEligibleDays } from "src/utils/promoUtils";
import { LiaTimesSolid } from "react-icons/lia";
import PromoCountdownTimer from "src/components/Promotions/PromoCountdownTimer";
import Markdown from "../Markdown";
import { MdInfo } from "react-icons/md";
import { TiWarning } from "react-icons/ti";


const DrawCard = ({ draw, promoData, subDrawData, error, isLoading, isEmpty, emptyMessage, mergeTickets }) => {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language;

  // Derive mergeTickets from promoData if not explicitly provided
  const shouldMergeTickets = mergeTickets ?? promoData?.mergeTickets ?? false;

  // Handle loading state
  if (isLoading) {
    return (
      <StyledCard className="loading">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="light" className="mb-3" />
          <p className="text-white">{t("promoDrawsPage.loadingPromos")}</p>
        </Card.Body>
      </StyledCard>
    );
  }

  // Handle error state
  if (error) {
    return (
      <StyledCard className="error">
        <Card.Body className="text-center py-5">
          <h5 className="text-white mb-3">
            <div className="mb-3 mustard">
              <TiWarning />
            </div>
            {t(`promoDrawsPage.errors.${error.code}`, {
              defaultValue: t('promoDrawsPage.errors.networkError')
            })}
          </h5>
          <p className="small grey">
            Error {error.code}
          </p>
        </Card.Body>
      </StyledCard>
    );
  }

  // Handle empty state
  if (isEmpty) {
    return (
      <StyledCard className="empty">
        <Card.Body className="text-center py-5">
          <h5 className="text-white mb-3">
            {t("promoDrawsPage.noDraws")}
          </h5>
          <Markdown className="grey small">
            {t("promoDrawsPage.noDrawsDescription")}
          </Markdown>
        </Card.Body>
      </StyledCard>
    );
  }

  // Ensure we have valid data before proceeding
  if (!draw || !promoData) {
    return (
      <StyledCard className="error">
        <Card.Body className="text-center py-5">
          <div className="error-icon mb-3">
            <span style={{ fontSize: '3rem', color: '#dc3545' }}>⚠️</span>
          </div>
          <h5 className="text-white mb-3">{t('promoDrawsPage.errors.invalidData')}</h5>
          <p className="text-muted small">{t('promoDrawsPage.errors.invalidDataDescription')}</p>
        </Card.Body>
      </StyledCard>
    );
  }

  // Keep this before the patch
  // asOf is used to show the date when the cache was last updated
  const asOf = formatTime(draw?.as_of, currentLocale);
  draw = addFourHoursPatch(draw);

  const { earning_start, earning_end, draw_end } = promoData || {};
  const promoStatus = determinePromoStatus(earning_start, earning_end, draw_end);

  // Handle merged tickets: combine all subDraws into a single draw
  const draws = shouldMergeTickets && subDrawData && subDrawData.length > 0
    ? [{
        total_tickets: subDrawData.reduce((sum, drawItem) => sum + (drawItem.total_tickets || 0), 0)
      }]
    : subDrawData && subDrawData.length > 0
      ? subDrawData
      : [draw]; // Single draw case: just use the draw object directly

  return (
    <StyledCard className={`${promoStatus} draw-card`}>
      <Card.Body className={`p-0 position-relative ${promoStatus}`}>
        <ResponsiveImage
          className="card-img-top"
          src={promoData.image}
          alt={draw?.promotion_name}
        />
      </Card.Body>
      <Card.Body className="p-0">
        <Card.Text className={`p-0 status-banner ${promoStatus}`}>
          {promoData.status?.[promoStatus] || t(`promoDrawsPage.status.${promoStatus}`, { defaultValue: promoStatus })}
        </Card.Text>
        <PromoCountdownTimer startAt={earning_start} endAt={earning_end} drawEnd={draw_end} />
      </Card.Body>
      <Card.Body className="perf-bottom">
        {/* Row 1: Map over draw columns (1 or 3) */}
        <TicketsRow className="m-0 ticket-counter">
          {draws.map((drawItem, index) => (
            <TicketCountCol
              key={drawItem.labelKey || index}
              className={`${promoStatus} ${drawItem.labelKey || ''}`}
            >
              <span>{drawItem.total_tickets}</span>
              {/* Optional category label for multi-draw promotions (hidden when shouldMergeTickets is true) */}
              {promoData.isMultiDraw && drawItem.labelKey && !shouldMergeTickets && (
                <div className="category-label">
                  {t(`promoDrawsPage.promos.${promoData.key}.label.${drawItem.labelKey}`, { defaultValue: drawItem.labelKey })}
                </div>
              )}
            </TicketCountCol>
          ))}
        </TicketsRow>

        {/* Row 2: "Tickets Earned" spanning all columns */}
        <Row className="m-0 mt-3">
          <Col>
            <TicketsEarnedContainer>
              <Markdown>
                {t("promoDrawsPage.ticketsEarned")}
              </Markdown>
              <OverlayTrigger placement="top" overlay={
                <Tooltip>
                  {t("promoDrawsPage.cacheDisclaimer")}
                  <hr/>
                  <small>
                    {t("promoDrawsPage.asOf", { date: asOf })}
                  </small>
                </Tooltip>
              }>
                <InfoIconTrigger className="overlay-trigger">
                  <MdInfo />
                </InfoIconTrigger>
              </OverlayTrigger>
            </TicketsEarnedContainer>
          </Col>
        </Row>
        <PerfBottomEdges/>
      </Card.Body>
      <Card.Body className="pt-4">
        <Card.Title>{promoData.title || draw?.promotion_name}</Card.Title>
        <Card.Text className="small mutedwhite drawDates d-none">
            <span className="text-nowrap">
              {formatDate(earning_start, currentLocale)} {formatTimeString(earning_start, currentLocale)}
            </span>
            <span className="text-nowrap">
              {" - "}
              {formatDate(earning_end, currentLocale)} {formatTimeString(earning_end, currentLocale)}
            </span>
        </Card.Text>
          <Markdown className="small mutedwhite draw-description">{promoData.description || ""}</Markdown>
        <Card.Text className="small">
            <Link to={t(`routes.${promoData?.key}`)} className="bluelinkdark">
              <strong>
                {t("promoDrawsPage.rules")}
              </strong>
            </Link>
        </Card.Text>
      </Card.Body>
      <Card.Body className="play-days perf-bottom">
        <p className="small mutedwhite">
          {t("promoDrawsPage.playDays")}
        </p>
        {promoData.eligiblePlayPeriods?.description && (
          <Markdown className="text mb-3">
            {promoData.eligiblePlayPeriods.description}
          </Markdown>
        )}
        {!promoData.eligiblePlayPeriods?.hideDefaultSchedule && (
          <>
            <Row className="eligible-days m-0 mx-auto">
              {getEligibleDays(draw).map(({ key, active }) => (
                <Col className="p-0" key={key}>
                  <span className={`day-circle ${active ? "active" : "inactive"}`}>
                    <span>
                      {t(`promoDrawsPage.daysOfWeek.letters.${key}`)}
                    </span>
                    <LiaTimesSolid />
                  </span>
                </Col>
              ))}
            </Row>
            <p className="mt-2">
              {draw?.start_hour && draw?.end_hour
                ? `${convertTimeTo24HourFormat(draw?.start_hour, currentLocale)} - ${convertTimeTo24HourFormat(draw?.end_hour, currentLocale)}`
                : t("promoDrawsPage.allDay")}
            </p>
            <Row className="m-0 mt-2 play-times">
              <Col className="time-holder">
              </Col>
            </Row>
          </>
        )}
        <PerfBottomEdges/>
      </Card.Body>
      <Card.Body>
        <Button
          className="m-0"
          label={t("promoDrawsPage.cashGames")}
          href={t("routes.cashGames")}
          specialHover
          bgColor="#442c05"
          textColor="#fff"
          size="small"
        />
      </Card.Body>
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  text-align: center;
  margin: 0 0 2rem;
  p.status-banner {
    margin: 0;
    text-align: center;
    width: 100%;
    font-size: 12px;
    text-transform: uppercase;
    font-weight: bold;
    color: #ffffff;
    &.starting {
      background: #583c059e;
    }
    &.during, &.drawing {
      background: #0658059e;
    }
    &.closed {
      background: #0000009e;
    }
  }

  .card-body {
    border-bottom: solid 1px #0f1112;
    &.perf-bottom {
      position: relative;
      border-bottom: solid 2px #000;
      &::after {
        content: "";
        display: block;
        position: absolute;
        bottom: -11px;
        left: 0;
        width: 100%;
        height: 20px;
        background-image: radial-gradient(circle at 10px 10px, #000 5px, transparent 5px);
        background-size: 20px 20px;
        background-repeat: repeat-x;
        background-position: center bottom;
      }
    }
  }
  .eligible-days {
    max-width: 20rem;
  }
  .play-days {
  }
  .play-times {
    text-align: center;
    .col {
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .icon-holder {
      flex: 0 0 12.5%; /* 1/8 */
      max-width: 12.5%;
      display: flex;
      align-items: center;
    }

    .time-holder {
      flex: 0 0 87.5%; /* 7/8 */
      max-width: 87.5%;
      padding-right: 12.5%;
      p {
        margin: 0;
      }
    }
    svg {
      font-size: 1.6rem;
      color: #f5a623;
    }
  }
  .draw-description p strong {
    color: #f5a623;
  }

  .day-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: clamp(1.6rem, 2.4vw, 2.5rem);
    height: clamp(1.6rem, 2.4vw, 2.5rem);
    line-height: clamp(1.6rem, 2.4vw, 3.2rem);
    font-size: clamp(0.8rem, 2vw, 1rem);
    border-radius: 50%;
    margin: 0 auto;
    font-size: 0.85rem;
    font-weight: bold;
    span {
      position: relative;
      z-index: 2;
    }
  }

  .day-circle.active {
    background-color: green;
    color: white;
    svg {
      display: none;
    }
  }

  .day-circle.inactive {
    background-color: #0f1112;
    color: var(--mutedwhite);
    position: relative;
    z-index: 2;
    svg {
      position: absolute;
      bottom: -5px;
      right: -6px;
      width: clamp(1.6rem, 1.2vw, 3.2rem);
      height: clamp(1.6rem, 1.2vw, 3.2rem);
      line-height: clamp(1.6rem, 2.4vw, 3.2rem);
      font-size: clamp(0.8rem, 2vw, 1rem);
      z-index: 2;
      color: #720909;
      opacity: 0.8;
    }
  }
`;

const PerfBottomEdges = styled.div`
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 20px;

  &:before, &:after {
    content: "";
    display: block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #000;
    position: absolute;
    bottom: 0;
    left: -10px;
  }
  &:after {
    left: auto;
    right: -10px;
  }
`;

const TicketsRow = styled(Row)`
  margin: 0;
`;

const TicketCountCol = styled(Col)`
text-align: center;
  font-size: 1rem;
  line-height: 1.2rem;
  span {
    font-weight: bold;
    font-size: 4rem;
    line-height: 4rem;
    color: #f5a623;
  }
  &.starting, &.starting span {
    color: grey;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 0 1rem;

  .category-label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0.2rem 0 0.9rem;
    color: #f5a623;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const TicketsEarnedContainer = styled.div`
  position: relative;
  display: inline-block;
  margin: 0 auto;
`;

const InfoIconTrigger = styled.p`
  padding-bottom: 2px;
  font-size: 0.8rem;
  line-height: 1rem;
  position: absolute;
  top: -2px;
  right: -0.9rem;
  border-bottom: none;
  opacity: 0.4;
  &:hover {
    opacity: 0.9;
  }
`;

export default DrawCard;
